'use client';

import { Button, Select } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { removeItems, updateLineQuantity } from '@shared/components/cart/actions';
import BDImage from '@shared/components/elements/bd-image';
import { Spinner } from '@shared/components/layout/spinner';
import { LineTotalWithDiscount } from '@shared/components/price-line';
import { CartItem, Money } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import { getCartLineLink } from '@shared/lib/utils/cart-utils';
import { rOptionName } from '@shared/lib/utils/formats';
import { addMoney, renderMoney } from '@shared/lib/utils/money';
import clsx from 'clsx';
import Link from 'next/link';
import { useActionState, useState } from 'react';

const EditForm = ({
  canUpdate,
  canRemove,
  setQuantity,
  isFirstTimeOrder,
  lineItem,
  updateCartAction,
  pendingUpdate,
  removeCartAction,
  pendingRemove,
  totalAmount,
  className,
}: {
  canUpdate: boolean;
  canRemove: boolean;
  setQuantity: (quantity: number) => void;
  isFirstTimeOrder: boolean;
  lineItem: CartItem;
  updateCartAction: (payload: FormData) => void;
  pendingUpdate: boolean;
  removeCartAction: (payload: FormData) => void;
  pendingRemove: boolean;
  totalAmount: Money;
  className?: string;
}) => {
  return (
    <div
      id="bottom-part"
      className={clsx(
        'border-bd-contrast @container mt-2 flex flex-row flex-wrap justify-between gap-2 border-t pt-2',
        className
      )}
    >
      <div className="flex flex-row items-center justify-end gap-2 text-sm">
        {canUpdate && (
          <form action={updateCartAction}>
            <label htmlFor="quantity" className="sr-only">
              Quantity, {lineItem.quantity}
            </label>
            <Select
              id="quantity"
              name="quantity"
              disabled={pendingUpdate}
              onChange={(e) => {
                const quantity = parseInt(e.target.value);
                setQuantity(quantity);
                setTimeout(() => {
                  e.preventDefault();
                  e.target.form?.requestSubmit();
                }, 0);
              }}
              key={`${lineItem.id}-${lineItem.quantity}`}
              defaultValue={lineItem.quantity}
              className="border-bd-contrast text-bd-dark/80 focus:border-bd-primary-light focus:ring-bd-primary-light focus:outline-hidden min-w-12 max-w-full cursor-pointer rounded-sm border px-2 py-1 text-left text-sm leading-4 focus:ring-1 md:text-sm"
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
              <option value={5}>5</option>
            </Select>
          </form>
        )}
        {canRemove && (
          <form action={removeCartAction}>
            <Button
              className="hover:border-bd-dark/80 ml-auto flex flex-row items-center gap-x-1 border-b border-transparent text-sm"
              type="submit"
              disabled={pendingRemove}
            >
              <XMarkIcon aria-hidden="true" className="h-5 w-5" />
              <span className="@[18em]:block hidden">Remove</span>
            </Button>
          </form>
        )}
      </div>

      <div className="mt-1 flex justify-end gap-x-2 text-sm">
        <span className="">{isFirstTimeOrder ? 'Quoted Total: ' : 'Total:'}</span>
        {renderMoney(totalAmount)}
      </div>
    </div>
  );
};

export default function InCartListItem({
  lineItem,
  secondaryLineItem,
  operatingCart,
  isFirstTimeOrder = false,
  additionalInfo,
  canRemove = true,
  canUpdate = true,
}: {
  lineItem: CartItem;
  secondaryLineItem?: CartItem;
  operatingCart: Cart;
  isFirstTimeOrder?: boolean;
  additionalInfo?: React.ReactNode;
  canRemove?: boolean;
  canUpdate?: boolean;
}) {
  const url = getCartLineLink(lineItem);
  const [quantity, setQuantity] = useState(lineItem.quantity);
  const subtotalAmount = addMoney(
    lineItem.cost.subtotalAmount,
    secondaryLineItem?.cost.subtotalAmount
  );
  const totalAmount = addMoney(lineItem.cost.totalAmount, secondaryLineItem?.cost.totalAmount);
  const [updateError, updateCartAction, pendingUpdate] = useActionState(
    updateLineQuantity.bind(null, {
      cartId: operatingCart.id,
      lineId: lineItem.id,
      quantity: quantity,
    }),
    null
  );
  const [removeError, removeCartAction, pendingRemove] = useActionState(
    removeItems.bind(null, {
      cartId: operatingCart.id,
      lineIds: [lineItem.id, secondaryLineItem?.id].filter(Boolean) as string[],
    }),
    null
  );

  return (
    <section
      aria-labelledby="cart-item-charges-header"
      className="border-bd-contrast @container/diamond-cart-item relative flex w-full flex-col justify-between rounded-md border p-2 md:p-6"
      key={lineItem.id}
    >
      {(pendingUpdate || pendingRemove) && <Spinner className="h-12 w-12" />}

      <div className="relative grid w-full grid-cols-3 gap-4">
        <div className="border-bd-contrast relative mx-auto aspect-square w-full rounded-md border md:mr-2 lg:mr-4">
          {(() => {
            const image = lineItem.merchandise.image;
            return (
              image && (
                <BDImage
                  alt={image.altText || lineItem.merchandise.title}
                  src={image}
                  className="h-full w-full rounded-xl object-cover object-center"
                />
              )
            );
          })()}
          {(() => {
            const image = secondaryLineItem?.merchandise.image;
            return (
              image && (
                <BDImage
                  alt={image.altText || secondaryLineItem?.merchandise.title}
                  src={image}
                  className="absolute bottom-0 right-0 h-1/3 w-1/3 rounded-xl object-cover object-center"
                />
              )
            );
          })()}
        </div>

        <div className="col-span-2 flex flex-col justify-between md:ml-2 lg:ml-4">
          <div id="top-part" className="flex flex-col gap-1">
            {url ? (
              <Link href={url}>
                <h2 className="text-base">
                  {lineItem.merchandise.product.title}
                  {lineItem.quantity > 1 && ` × ${lineItem.quantity}`}
                </h2>
              </Link>
            ) : (
              <h2 className="text-base">
                {lineItem.merchandise.product.title}
                {lineItem.quantity > 1 && ` × ${lineItem.quantity}`}
              </h2>
            )}
            {lineItem.quantity > 1 && (
              <p className="text-sm">Each: {renderMoney(lineItem.cost.amountPerQuantity)}</p>
            )}
            <div>
              <LineTotalWithDiscount lineItem={lineItem} />
            </div>

            <div className="flex flex-col">
              {lineItem.merchandise.selectedOptions.map((option) => (
                <p key={option.name} className="text-xs text-slate-500">
                  {rOptionName(option)}: {option.value}
                </p>
              ))}
              {additionalInfo}
            </div>
            {secondaryLineItem && (
              <>
                <h2 className="text-base">
                  w/ {secondaryLineItem.merchandise.product.title}
                  {secondaryLineItem.quantity > 1 && ` × ${secondaryLineItem.quantity}`}
                </h2>

                <div>
                  <LineTotalWithDiscount lineItem={secondaryLineItem} />
                </div>
                <div className="flex flex-col">
                  {secondaryLineItem.merchandise.selectedOptions.map((option) => (
                    <p key={option.name} className="text-xs text-slate-500">
                      {rOptionName(option)}: {option.value}
                    </p>
                  ))}
                </div>
              </>
            )}
          </div>

          <EditForm
            canUpdate={canUpdate}
            canRemove={canRemove}
            setQuantity={setQuantity}
            isFirstTimeOrder={isFirstTimeOrder}
            lineItem={lineItem}
            updateCartAction={updateCartAction}
            pendingUpdate={pendingUpdate}
            removeCartAction={removeCartAction}
            pendingRemove={pendingRemove}
            totalAmount={totalAmount}
            className={'@[25em]/diamond-cart-item:flex col-span-2 hidden'}
          />
        </div>
      </div>
      <EditForm
        canUpdate={canUpdate}
        canRemove={canRemove}
        setQuantity={setQuantity}
        isFirstTimeOrder={isFirstTimeOrder}
        lineItem={lineItem}
        updateCartAction={updateCartAction}
        pendingUpdate={pendingUpdate}
        removeCartAction={removeCartAction}
        pendingRemove={pendingRemove}
        totalAmount={totalAmount}
        className={'@[25em]/diamond-cart-item:hidden'}
      />
    </section>
  );
}
